import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { BRAND_LOGO } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const HERO = "/assets/dubai-skyline-hero.jpg";
const ROLE_LABEL = { administrator: "Administrator", relationship_manager: "Relationship Manager", operations: "Operations", compliance: "Compliance", customer: "Customer" };

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [demoPw, setDemoPw] = useState("");

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);
  useEffect(() => {
    api.get("/demo-accounts").then((r) => { setAccounts(r.data.accounts); setDemoPw(r.data.password); }).catch(() => {});
  }, []);

  const submit = async (e, em, pw) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const u = await login(em ?? email, pw ?? password);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "customer" ? "/onboarding" : "/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Dubai skyline across the waterfront at dusk" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B192C]/95 via-[#064E3B]/85 to-[#0B192C]/97" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src={BRAND_LOGO} alt="Barjeel" className="h-11 w-11 rounded-lg bg-white p-1 object-contain" />
            <div><div className="font-display text-2xl font-bold">Barjeel</div><div className="text-xs text-white/70">Technology by REEDOS Software Solutions Pvt. Ltd.</div></div>
          </div>
          <div>
            <div className="overline-label text-gold mb-3">One Barjeel One Platform</div>
            <h1 className="font-display text-5xl font-bold leading-tight max-w-md">Unified wealth, onboarding & compliance.</h1>
            <p className="text-white/70 mt-4 max-w-md text-sm">Wealth management across the UAE and international markets—with digital onboarding, a 360° customer view, compliance screening, and MIS in one unified prototype.</p>
          </div>
          <div className="text-xs text-white/50">Prototype · Synthetic data · All verifications simulated in MOCK mode.</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-start gap-3 mb-8">
            <img src={BRAND_LOGO} alt="Barjeel" className="h-10 w-10 rounded-lg object-contain shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-2xl font-bold text-primary leading-tight">Barjeel</div>
              <div className="text-xs font-semibold text-gold leading-snug break-words">One Barjeel One Platform</div>
              <div className="text-[10px] text-muted-foreground">Technology by REEDOS</div>
            </div>
          </div>
          <h2 className="font-display text-3xl font-bold">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Access the staff portal or customer journey.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@barjeel.demo" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1" />
            </div>
            <Button type="submit" disabled={busy} className="w-full" data-testid="login-submit">{busy ? "Signing in…" : "Sign in"}</Button>
          </form>

          <div className="mt-8">
            <div className="overline-label text-muted-foreground mb-3">Quick demo logins · password {demoPw}</div>
            <div className="grid grid-cols-1 gap-2">
              {accounts.map((a, idx) => {
                // Build a unique, deterministic testid even when roles repeat
                // (e.g., two customer accounts). First occurrence keeps `demo-login-<role>`
                // for backward compatibility; subsequent occurrences get a numeric suffix.
                const sameRoleIdx = accounts.slice(0, idx).filter(x => x.role === a.role).length;
                const tid = sameRoleIdx === 0 ? `demo-login-${a.role}` : `demo-login-${a.role}${sameRoleIdx + 1}`;
                return (
                <button key={a.email} data-testid={tid}
                  onClick={(e) => submit(e, a.email, demoPw)}
                  className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5 text-left hover:border-primary transition-colors">
                  <div>
                    <div className="text-sm font-medium">{a.name}</div>
                    <div className="text-xs text-muted-foreground">{a.email}</div>
                  </div>
                  <span className="overline-label text-gold">{ROLE_LABEL[a.role]}</span>
                </button>
              );})}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
